import { useEffect } from "react";
import { Link } from "react-router";

import {
  Column,
  MoreTabDropdownItem,
  TopbarContainer,
  TopbarTitle,
} from "@/components";
import { CompanyComments } from "@/components/companies/comments/CompanyComments";
import { CompanyIntakeContainer } from "@/components/companies/company-intake/CompanyIntakeContainer";
import { CompanyInternalConfig } from "@/components/companies/company-internal-config/CompanyInternalConfig";
import { CompanyFiles } from "@/components/companies/files/CompanyFiles";
import { CompanyNotes } from "@/components/companies/notes/CompanyNotes";
import { CompanyProfile } from "@/components/companies/profile/CompanyProfile";
import { CompanySettings } from "@/components/companies/settings/CompanySettings";
import { JobsFiltersAndTable } from "@jobs/JobsFiltersAndTable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useSelectedTabFromUrl } from "@/hooks/useSelectedTabFromUrl";
import { useSetTitle } from "@/hooks/useSetTitle";
import { useGetCompanyInternalConfig } from "@/hooks/queries/useGetCompanyInternalConfig";
import { useGetCachedCompaniesSummary } from "@/hooks/sets/useGetCachedCompaniesSummary";
import { misc } from "@/lib/misc";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";

const MORE_TABS = [{ value: "internal-config", label: "Internal Config" }];

export const CompanyPage = () => {
  const currentCompanyId = useCurrentCompanyId();
  const companies = useGetCachedCompaniesSummary();
  const { data: companyInternalConfig } = useGetCompanyInternalConfig();
  const { tabValue, setTabValue } = useSelectedTabFromUrl({
    defaultValue: "jobs",
    paramName: "t", // TODO: Should we use "ct" to differentiate from other uses?
  });

  const currentCompany = companies.find(
    (company) => company.id === currentCompanyId,
  );

  useSetTitle({
    enabled: !!currentCompany,
    // NOTE: The title won't be updated if the company is not found.
    title: `${currentCompany?.name ?? "Company"} | ${misc.toTitleCase(tabValue)}`,
  });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.COMPANY);
  }, []);

  if (!currentCompany) {
    // TODO: Error state.
    return null;
  }

  return (
    <Column className="h-svh">
      <TopbarContainer>
        <TopbarTitle className="!text-slate-500">
          <Link
            className="hover:underline"
            // TODO: Should really have a util that serves as a source of truth for the routes.
            to={`/companies`}
          >
            Companies
          </Link>{" "}
          / <span className="text-slate-700">{currentCompany.name}</span>
        </TopbarTitle>
      </TopbarContainer>
      <Tabs
        className="flex min-h-0 flex-1 flex-col gap-6 pt-6"
        onValueChange={(newTab) => {
          mixpanelUtils.trackTabView(MIXPANEL_SCREENS.COMPANY, newTab);
          setTabValue(newTab);
        }}
        value={tabValue}
      >
        <div className="px-6">
          <TabsList>
            {/* TODO: Better mechanism for setting the width of the tabs and ensuring they do not shift when switching.  */}
            <TabsTrigger className="w-[65px]" value="jobs">
              Jobs
            </TabsTrigger>
            <TabsTrigger value="intake">Company Intake</TabsTrigger>
            <TabsTrigger className="w-[72.5px]" value="notes">
              Notes
            </TabsTrigger>
            <TabsTrigger className="w-[105px]" value="comments">
              Comments
            </TabsTrigger>
            <TabsTrigger className="w-[65px]" value="files">
              Files
            </TabsTrigger>
            <TabsTrigger className="w-[78px]" value="profile">
              Profile
            </TabsTrigger>
            <TabsTrigger className="w-[87.5px]" value="settings">
              Settings
            </TabsTrigger>
            <MoreTabDropdownItem tabs={MORE_TABS} value={tabValue} />
          </TabsList>
        </div>
        <TabsContent className="min-h-0" value="jobs">
          <JobsFiltersAndTable className="h-full p-6 pt-0" />
        </TabsContent>
        <TabsContent className="min-h-0" value="notes">
          <CompanyNotes className="h-full p-6 pt-0" />
        </TabsContent>
        <TabsContent value="comments">
          <CompanyComments className="p-6 pt-0" />
        </TabsContent>
        <TabsContent className="min-h-0" value="files">
          <CompanyFiles className="h-full p-6 pt-0" />
        </TabsContent>
        <TabsContent value="profile">
          <CompanyProfile className="p-6 pt-0" company={currentCompany} />
        </TabsContent>
        <TabsContent value="settings">
          <CompanySettings className="p-6 pt-0" />
        </TabsContent>
        <TabsContent className="min-h-0" value="intake">
          <CompanyIntakeContainer
            companyId={currentCompanyId}
            companyName={currentCompany.name}
          />
        </TabsContent>
        <TabsContent value="internal-config">
          <CompanyInternalConfig
            className="p-6"
            companyInternalConfig={companyInternalConfig}
          />
        </TabsContent>
      </Tabs>
    </Column>
  );
};
